/**
 * 2.0 wave 2, W2-CHROME (spec 7.5) — the account VIEW: Training, Coaching or
 * Guardian. One choice, remembered per device, that decides which tab set the
 * account sees and which tab it lands on.
 *
 * ## What lives here, and what does not
 *
 * Pure functions and the one device key. No React: `hooks/useAccountView.ts`
 * is the hook, `lib/nav/tabSets.ts` the tab sets. The key is the one writer
 * `lib/deviceState.ts` removes on account deletion (CH-10), through
 * `clearStoredAccountView()` below, so the inventory and the writer cannot
 * drift.
 *
 * ## The rules, in code
 *
 *  · Options come ONLY from the account's roles, in a fixed order: Training,
 *    Coaching, Guardian (`accountViewOptions`). A view the account does not
 *    have is never an option, so it can be neither selected nor resolved.
 *  · The stored value is stamped with the user id and is ignored when it names
 *    another user, an unknown view or a view outside the account's options
 *    (`readStoredAccountView`, `resolveAccountView`).
 *  · With nothing usable stored the default is the first option: Training if
 *    available, else Coaching, else Guardian (CH-3).
 *  · Choosing a view is a device-local write. Nothing here fetches or changes
 *    any server-side authorization (CH-11): the view is presentation only, and
 *    every boundary keeps reading the server's role fact.
 *
 * ## Why the module keeps a memory mirror
 *
 * `localStorage` throws in some private modes. The mirror keeps the switch
 * working for the life of the page instead of silently doing nothing.
 */

import { authMetadataName, type DisplayNameUser } from './displayName';

export type AccountViewId = 'training' | 'coaching' | 'guardian';

/** The fixed option order. */
export const ACCOUNT_VIEW_IDS: readonly AccountViewId[] = ['training', 'coaching', 'guardian'];

/** The device key (CH-10). Cleared by `clearOnAccountDeletion()`, not by sign out. */
export const ACCOUNT_VIEW_KEY = 'tayro.accountView.v1';

/** Appendix C 46, 47, 48: the option labels. */
export const ACCOUNT_VIEW_LABELS: Readonly<Record<AccountViewId, string>> = {
  training: 'Training',
  coaching: 'Coaching',
  guardian: 'Guardian',
};

export function isAccountViewId(value: unknown): value is AccountViewId {
  return typeof value === 'string' && (ACCOUNT_VIEW_IDS as readonly string[]).includes(value);
}

/** What the account has. The caller derives these from the role fact and the guardian check. */
export interface AccountViewFacts {
  athleteSide: boolean;
  coachSide: boolean;
  guardianSide: boolean;
}

/** The options an account may choose between, always in the fixed order. */
export function accountViewOptions(facts: AccountViewFacts): AccountViewId[] {
  const out: AccountViewId[] = [];
  if (facts.athleteSide) out.push('training');
  if (facts.coachSide) out.push('coaching');
  if (facts.guardianSide) out.push('guardian');
  return out;
}

/**
 * The stored choice when it is usable, else the default. `stored` is whatever
 * `readStoredAccountView` returned. A value outside `options` is ignored.
 */
export function resolveAccountView(
  options: readonly AccountViewId[],
  stored: AccountViewId | null,
): AccountViewId {
  if (stored && options.includes(stored)) return stored;
  return options[0] ?? 'training';
}

// ── The device slot ──────────────────────────────────────────────────────

let memory: string | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of Array.from(listeners)) listener();
}

/** The raw stored string, or '' when none. Stable between writes, so it is a valid
 *  `useSyncExternalStore` snapshot. Storage is the source of truth; the memory
 *  mirror answers only when storage cannot be read at all. */
export function readAccountViewRaw(): string {
  try {
    return window.localStorage.getItem(ACCOUNT_VIEW_KEY) ?? '';
  } catch {
    return memory ?? '';
  }
}

/**
 * Parse the raw slot for one user. Null for anything that is not a view
 * stamped with exactly this user id.
 */
export function parseStoredAccountView(
  raw: string,
  userId: string | null | undefined,
): AccountViewId | null {
  if (!raw || !userId) return null;
  try {
    const parsed = JSON.parse(raw) as { userId?: unknown; view?: unknown } | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.userId !== userId) return null;
    return isAccountViewId(parsed.view) ? parsed.view : null;
  } catch {
    return null;
  }
}

export function readStoredAccountView(userId: string | null | undefined): AccountViewId | null {
  return parseStoredAccountView(readAccountViewRaw(), userId);
}

/** THE writer. Stamps the user id; tells every subscribed hook. */
export function writeStoredAccountView(userId: string, view: AccountViewId): void {
  const raw = JSON.stringify({ userId, view });
  memory = raw;
  try {
    window.localStorage.setItem(ACCOUNT_VIEW_KEY, raw);
  } catch {
    /* the mirror carries it */
  }
  notify();
}

/** Removes the slot. Called by `clearOnAccountDeletion()`; sign out never calls it. */
export function clearStoredAccountView(): void {
  memory = null;
  try {
    window.localStorage.removeItem(ACCOUNT_VIEW_KEY);
  } catch {
    /* best effort */
  }
  notify();
}

/** `useSyncExternalStore` subscription: in-page writes plus other tabs' `storage` events. */
export function subscribeAccountView(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (e: StorageEvent): void => {
    if (e.key === null || e.key === ACCOUNT_VIEW_KEY) listener();
  };
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/**
 * The full name for the menu identity (CH-9, Appendix C 43): the athlete
 * context `Name`, else the sign-in provider's name (`authMetadataName`), else
 * ''. NEVER derived from the email: a name the client cannot vouch for is no
 * name (B383 is a server-side fix).
 */
export function accountFullName(
  user: DisplayNameUser | null | undefined,
  raw: Record<string, unknown> | null | undefined,
): string {
  const formal = raw && typeof raw.Name === 'string' ? raw.Name.trim() : '';
  return formal || authMetadataName(user);
}
